import { notifySentry } from 'utils/error/notifySentry';
import AppRoutes from './AppRoutes';
import checkStepCondition from './utils/checkStepCondition';
import { isB2BFlow, isComplexFlow, isB2CFlow } from './utils/conditions';
import getOrderedSteps from './utils/getOrderedSteps';
import { ConditionalStep, ConditionParams } from './utils/types';

// NOTE: this contains the slides that are not questions, in order to be able to calculate the amount of questions in the flow.
export const notQuestionSteps = [
  AppRoutes.SUMMARY,
  AppRoutes.ESTIMATE,
  AppRoutes.THANKS,
];
export const noBreadcrumbsItems = [AppRoutes.ESTIMATE, AppRoutes.THANKS];

export const allConditionalSteps: ConditionalStep[] = [
  {
    step: AppRoutes.BUSINESS_CHOICE,
    answerKeys: 'businessChoice',
  },
  {
    step: AppRoutes.FLOW_CHOICE,
    answerKeys: 'flowChoice',
  },
  {
    step: AppRoutes.ADDRESS_CHOICE,
    // NOTE: this means that address and addressDetails should be defined after this step
    answerKeys: ['address', 'addressDetails'],
  },
  {
    step: AppRoutes.PANEL_SELECTION,
    answerKeys: 'panelId',
  },
  {
    step: AppRoutes.ROOF_ANGLE,
    answerKeys: 'roofAngle',
  },
  {
    step: AppRoutes.ROOF_TYPE,
    answerKeys: 'roofType',
  },
  {
    step: AppRoutes.CONSUMPTION,
    // NOTE: this means that either consumption OR billAmount should be defined after this step
    answerKeys: [['consumption'], ['billAmount']],
  },
  {
    step: AppRoutes.MAP_SYSTEM,
    condition: isComplexFlow,
    // NOTE: the mapDetails can be skipped and thus undefined
  },
  {
    step: AppRoutes.COUNTER,
    condition: isComplexFlow,
    answerKeys: 'counter',
  },
  // {
  //   step: AppRoutes.INVERTER,
  //   condition: isComplexFlow,
  //   answerKeys: 'inverter'
  // },
  {
    step: AppRoutes.BATTERY_OPTION,
    condition: (state) => isB2CFlow(state) && isComplexFlow(state),
    answerKeys: 'needBattery',
  },
  {
    step: AppRoutes.ELECTRIC_VEHICLE,
    condition: (state) => isB2CFlow(state) && isComplexFlow(state),
    answerKeys: 'needVehicle',
  },
  {
    step: AppRoutes.EXTRA_SERVICES,
    // NOTE: show only on b2b flow
    condition: (state) => isB2BFlow(state),
  },
  {
    step: AppRoutes.SUMMARY,
  },
  {
    step: AppRoutes.THANKS,
  },
  {
    step: AppRoutes.ESTIMATE,
  },
];

export const getActiveSteps = (
  allSteps: ConditionalStep[],
  state: ConditionParams
) => {
  const activeSteps = allSteps.filter((step) =>
    checkStepCondition(step.condition, state)
  );

  const parsedStepOrderSteps = activeSteps.map((step) => {
    const validStepOrders =
      step.stepOrder?.filter((stepOrder) =>
        checkStepCondition(stepOrder.condition, state)
      ) ?? [];

    // NOTE: make sure that only one condition is met for each step for the stepOrder.
    if (validStepOrders.length > 1) {
      throw new Error(
        `More than one step order condition is met for step ${step.step}`
      );
    }

    return {
      ...step,
      stepOrder: validStepOrders,
    };
  });

  // NOTE: make sure that no two steps come after the same step (no duplicate stepOrders).
  parsedStepOrderSteps.reduce((acc, step) => {
    if (!step.stepOrder) return acc;
    step.stepOrder.forEach((elem) => {
      if (acc.has(elem.previousStep)) {
        console.error(
          'parsedStepOrderSteps',
          parsedStepOrderSteps,
          'step',
          step,
          'elem',
          elem
        );
        const error = new Error(
          'Two steps have the same previous step in stepOrder'
        );
        notifySentry(error);
        throw error;
      }
      acc.add(elem.previousStep);
    });
    return acc;
  }, new Set<string>());

  // NOTE: make sure that there is no infinite loop in the stepOrder.
  const visitedSteps = new Set<string>();

  const dfs = (step: ConditionalStep) => {
    if (visitedSteps.has(step.step)) {
      throw new Error('Infinite loop in stepOrder');
    }
    visitedSteps.add(step.step);
    step.stepOrder?.forEach((elem) => {
      const nextStep = parsedStepOrderSteps.find(
        (elem2) => elem2.step === elem.previousStep
      );
      if (nextStep) {
        dfs(nextStep);
      }
    });
    visitedSteps.delete(step.step);
  };

  parsedStepOrderSteps.forEach((step) => {
    dfs(step);
  });

  const finalSteps: ConditionalStep[] = getOrderedSteps(parsedStepOrderSteps);

  return finalSteps;
};

export const getStepNamesAccordingToChoices = (
  allSteps: ConditionalStep[],
  state: ConditionParams
) => {
  const finalSteps = getActiveSteps(allSteps, state);
  return finalSteps.map((step) => step.step);
};
